import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Key, Copy, Check, Download, AlertCircle, Shield } from "lucide-react";
import api from "../../services/api";
import toast from "react-hot-toast";

interface SyncTokenData {
  token: string;
  tokenName: string;
  purpose: string;
  expiresIn: string;
  expiryDate: string;
  generatedAt: string;
  generatedBy: {
    id: string;
    name: string;
    email: string;
  };
  instructions: {
    usage: string;
    envVariable: string;
    scope: string;
  };
}

const SyncTokenGenerator: React.FC = () => {
  const [tokenName, setTokenName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState(365);
  const [generatedToken, setGeneratedToken] = useState<SyncTokenData | null>(
    null
  );
  const [copied, setCopied] = useState(false);

  const generateMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post("/auth/generate-sync-token", {
        tokenName: tokenName || undefined,
        expiresInDays,
      });
      console.log("API Response:", response);
      return response;
    },
    onSuccess: (data) => {
      console.log("Success data:", data);
      // The api interceptor already extracts response.data
      // So data is { success: true, message: "...", data: {...} }
      setGeneratedToken(data.data);
      toast.success("Sync token generated successfully!");
    },
    onError: (error: any) => {
      console.error("Generate token error:", error);
      toast.error(error.response?.data?.message || "Failed to generate token");
    },
  });

  const handleGenerate = () => {
    if (!tokenName.trim()) {
      toast.error("Please enter a token name");
      return;
    }
    generateMutation.mutate();
  };

  const handleCopy = () => {
    if (generatedToken) {
      navigator.clipboard.writeText(generatedToken.token);
      setCopied(true);
      toast.success("Token copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadEnvFile = () => {
    if (!generatedToken) return;

    const envContent = `# ZKTeco Attendance Sync Configuration
# Generated: ${new Date(generatedToken.generatedAt).toLocaleString()}
# Expires: ${new Date(generatedToken.expiryDate).toLocaleString()}
# Generated by: ${generatedToken.generatedBy.name} (${
      generatedToken.generatedBy.email
    })

# Branch Configuration
BRANCH_ID=your-branch-id-here
BRANCH_NAME=Your Branch Name

# API Configuration
CLOUD_API_URL=${window.location.origin}
API_TOKEN=${generatedToken.token}

# Database Configuration
ZKTECO_DB_PATH=C:\\Program Files (x86)\\ZKTime5.0\\att2000.mdb
ZKTECO_DB_COPY_PATH=C:\\Program Files (x86)\\ZKTime5.0\\att2000_copy.mdb

# Sync Settings
SYNC_INTERVAL=60000
COPY_INTERVAL=30000
BATCH_SIZE=100
USE_DB_COPY=true
`;

    const blob = new Blob([envContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `.env-sync-${tokenName
      .replace(/\s+/g, "-")
      .toLowerCase()}-${Date.now()}`;
    
    try {
      document.body.appendChild(a);
      a.click();
    } finally {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
    toast.success("Environment file downloaded!");
  };

  const handleReset = () => {
    setGeneratedToken(null);
    setTokenName("");
    setExpiresInDays(365);
    setCopied(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
            <Key className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">
              Sync Token Generator
            </h3>
            <p className="text-sm text-blue-100">
              Generate authentication tokens for branch attendance sync
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {!generatedToken ? (
          <div className="space-y-6">
            {/* Info Alert */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
              <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Cross-Branch Token</p>
                <p>
                  This token will have superadmin privileges and can sync
                  attendance data from <strong>any branch</strong>. Keep it
                  secure!
                </p>
              </div>
            </div>

            {/* Token Name Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Token Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                placeholder="e.g., Main Branch Sync Token"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
              <p className="text-xs text-gray-500 mt-1">
                A descriptive name to identify this token
              </p>
            </div>

            {/* Expiry Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Token Expiry
              </label>
              <select
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              >
                <option value={30}>30 days</option>
                <option value={90}>90 days (3 months)</option>
                <option value={180}>180 days (6 months)</option>
                <option value={365}>365 days (1 year) - Recommended</option>
                <option value={730}>730 days (2 years)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Token will expire after this period
              </p>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={generateMutation.isPending || !tokenName.trim()}
              className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              {generateMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Generating...
                </>
              ) : (
                <>
                  <Key className="h-5 w-5" />
                  Generate Token
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Success Message */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
              <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-green-800">
                <p className="font-semibold mb-1">
                  Token Generated Successfully!
                </p>
                <p>
                  Your sync token is ready. Copy it to the branch Windows PC's
                  .env file.
                </p>
              </div>
            </div>

            {/* Token Details */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 font-medium">Token Name</p>
                  <p className="text-gray-900 font-semibold">
                    {generatedToken.tokenName}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Expires In</p>
                  <p className="text-gray-900 font-semibold">
                    {generatedToken.expiresIn}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Purpose</p>
                  <p className="text-gray-900 font-semibold">
                    {generatedToken.purpose}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Scope</p>
                  <p className="text-gray-900 font-semibold">
                    {generatedToken.instructions.scope}
                  </p>
                </div>
              </div>
            </div>

            {/* Token Display */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Generated Token
              </label>
              <div className="relative">
                <div className="bg-gray-50 border border-gray-300 rounded-lg p-3 pr-12 font-mono text-xs break-all">
                  {generatedToken.token}
                </div>
                <button
                  onClick={handleCopy}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white border border-gray-300 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <Check className="h-5 w-5 text-green-600" />
                  ) : (
                    <Copy className="h-5 w-5 text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            {/* Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">Security Warning</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Keep this token secure and confidential</li>
                  <li>
                    Do not share it publicly or commit it to version control
                  </li>
                  <li>Token has superadmin privileges across all branches</li>
                  <li>Generate a new token if this one is compromised</li>
                </ul>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleDownloadEnvFile}
                className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                <Download className="h-5 w-5" />
                Download .env File
              </button>
              <button
                onClick={handleReset}
                className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-all border border-gray-300"
              >
                Generate New Token
              </button>
            </div>

            {/* Instructions */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <h4 className="font-semibold text-gray-900 text-sm">
                Setup Instructions:
              </h4>
              <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
                <li>Download the .env file or copy the token above</li>
                <li>
                  Add the token to your branch Windows PC's .env file as
                  API_TOKEN
                </li>
                <li>Update BRANCH_ID and BRANCH_NAME in the .env file</li>
                <li>Restart the sync service</li>
                <li>
                  The token works for all branches - just change the BRANCH_ID
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyncTokenGenerator;
